/**
 * Server-side open-round discovery (Option B). Polls the API's cheap
 * `/api/koth/current-round` (one shared poller behind it scans the chain) so the
 * normal lobby follows the keeper's rolling round ids WITHOUT every browser
 * hammering `getProgramAccounts`. Flat RPC load at any player count.
 *
 * Disabled in /watch broadcast mode — that path uses on-chain `useCurrentOpenRound`
 * directly. Returns the latest open round id, or null until the first poll lands.
 */
import { BN } from "@coral-xyz/anchor";
import { useEffect, useRef, useState } from "react";

const POLL_MS = 5000;

function apiBase(): string {
  const env = (import.meta as { env?: { VITE_API_URL?: string } }).env;
  return env?.VITE_API_URL ?? "http://localhost:3030";
}

export function useCurrentRoundApi(enabled: boolean): BN | null {
  const [roundId, setRoundId] = useState<BN | null>(null);
  const lastRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async (): Promise<void> => {
      try {
        const res = await fetch(`${apiBase()}/api/koth/current-round`, {
          cache: "no-store",
        });
        if (res.ok) {
          const j = (await res.json()) as { roundId?: string | null };
          const rid = j?.roundId ?? null;
          // Sticky: only advance to a new non-null id (the endpoint itself is
          // sticky across the settle→open gap, so we never flicker to null).
          if (!cancelled && rid && rid !== lastRef.current) {
            lastRef.current = rid;
            setRoundId(new BN(rid, 10));
          }
        }
      } catch {
        // transient network/API blip — keep the last id, retry next tick.
      }
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled]);

  return roundId;
}
